/**
 * Session assembler — turns a learner's spaced-repetition state and lesson
 * progress into a single day's practice plan.
 *
 * Pure functions only: `input.now` (epoch ms) and `mulberry32(input.seed)`
 * are the sole sources of "current time" and "randomness" — never
 * `Date.now()` or `Math.random()`. The same input always produces the same
 * {@link SessionPlan}.
 */

import { lessonById, lessonList } from "./content/lessons";
import { letterById } from "./content/registry";
import { sentenceById } from "./content/sentences";
import { wordById } from "./content/words";
import { mulberry32 } from "./prng";
import type { ReviewState } from "./srs";
import { isDue } from "./srs";

/** Practice-session duration in minutes; determines block sizing. */
export type SessionLength = 30 | 45;

/** User-configurable knobs for session assembly. */
export interface SessionSettings {
  length: SessionLength;
  paperEvery: number;
}

/** The kind of a single practice exercise within a block. */
export type ExerciseKind =
  | "letter-intro"
  | "sound-to-symbol"
  | "symbol-to-sound"
  | "barakhadi"
  | "word-reading"
  | "word-builder"
  | "paper-dictation"
  | "sentence-reading";

/** A single practice exercise: what kind of drill, on which item. */
export interface Exercise {
  kind: ExerciseKind;
  itemId: string;
}

/** A themed group of exercises within a session. */
export interface SessionBlock {
  kind: "review" | "lesson" | "reading" | "writing" | "paper" | "summary";
  lessonId?: string;
  exercises: Exercise[];
}

/** The full assembled plan for a single practice session. */
export interface SessionPlan {
  blocks: SessionBlock[];
  newLessonId: string | null;
}

/** Everything {@link assembleSession} needs to build a plan; nothing else. */
export interface AssembleInput {
  now: number;
  seed: number;
  settings: SessionSettings;
  reviews: ReviewState[];
  completedLessonIds: string[];
  sessionCount: number;
}

const REVIEW_CAP: Record<SessionLength, number> = { 30: 20, 45: 30 };
const READING_SIZE: Record<SessionLength, number> = { 30: 6, 45: 10 };
const LESSON_BLOCK_WORD_COUNT = 8;
const MAX_DUE_FOR_NEW_LESSON = 30;
const WRITING_BUILDER_MAX = 6;
const PAPER_DICTATION_COUNT = 6;
const STRUGGLING_WEIGHT = 3;
const STRUGGLING_LAPSES_THRESHOLD = 0;
const STRUGGLING_EASE_THRESHOLD = 2.3;

/** Kind of learnable item an id resolves to, or `null` if unrecognized. */
type ResolvedKind = "letter" | "word" | "sentence" | null;

function resolveKind(itemId: string): ResolvedKind {
  if (letterById.has(itemId)) return "letter";
  if (wordById.has(itemId)) return "word";
  if (sentenceById.has(itemId)) return "sentence";
  return null;
}

/** Fisher-Yates shuffle using an injected `[0, 1)` rand source. Non-mutating. */
function shuffle<T>(items: readonly T[], rand: () => number): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = copy[i];
    copy[i] = copy[j];
    copy[j] = tmp;
  }
  return copy;
}

/** Seeded sample of up to `count` unique items from `pool`, without replacement. */
function sampleUnique<T>(
  pool: readonly T[],
  count: number,
  rand: () => number,
): T[] {
  return shuffle(pool, rand).slice(0, Math.max(0, count));
}

/**
 * Build a weighted pool where "struggling" items (a review state with
 * `lapses > 0` or `ease < 2.3`) appear {@link STRUGGLING_WEIGHT} times and all
 * others appear once, then draw a seeded sample without replacement (deduped).
 */
function weightedSample(
  ids: readonly string[],
  count: number,
  reviewByItemId: ReadonlyMap<string, ReviewState>,
  rand: () => number,
): string[] {
  const pool: string[] = [];
  for (const id of ids) {
    const review = reviewByItemId.get(id);
    const isStruggling =
      review !== undefined &&
      (review.lapses > STRUGGLING_LAPSES_THRESHOLD ||
        review.ease < STRUGGLING_EASE_THRESHOLD);
    const weight = isStruggling ? STRUGGLING_WEIGHT : 1;
    for (let i = 0; i < weight; i += 1) {
      pool.push(id);
    }
  }
  const shuffled = shuffle(pool, rand);
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of shuffled) {
    if (seen.has(id)) continue;
    seen.add(id);
    result.push(id);
    if (result.length >= count) break;
  }
  return result;
}

/** Exercise kind for a review-block item, by its resolved content kind. */
function reviewExerciseKind(
  itemId: string,
  index: number,
): ExerciseKind | null {
  const kind = resolveKind(itemId);
  if (kind === "letter") {
    const letter = letterById.get(itemId);
    if (letter?.kind === "matra") return "barakhadi";
    return index % 2 === 0 ? "sound-to-symbol" : "symbol-to-sound";
  }
  if (kind === "word") return "word-reading";
  if (kind === "sentence") return "sentence-reading";
  return null;
}

function buildReviewBlock(
  reviews: readonly ReviewState[],
  now: number,
  cap: number,
): { block: SessionBlock | null; dueCount: number } {
  const due = reviews.filter((state) => isDue(state, now));
  const sorted = [...due].sort((a, b) => a.dueAt - b.dueAt);
  const capped = sorted.slice(0, cap);

  const exercises: Exercise[] = [];
  let index = 0;
  for (const state of capped) {
    const kind = reviewExerciseKind(state.itemId, index);
    if (kind === null) continue; // unknown id — skip defensively
    exercises.push({ kind, itemId: state.itemId });
    index += 1;
  }

  if (due.length === 0) {
    return { block: null, dueCount: due.length };
  }
  return {
    block: { kind: "review", exercises },
    dueCount: due.length,
  };
}

function nextUncompletedLesson(completedLessonIds: readonly string[]) {
  const completed = new Set(completedLessonIds);
  return lessonList.find((lesson) => !completed.has(lesson.id)) ?? null;
}

/**
 * The most curriculum-advanced completed lesson, per `lessonList` order
 * (independent of the order ids appear in `completedLessonIds`), or `null`
 * if none have been completed.
 */
function mostRecentCompletedLesson(completedLessonIds: readonly string[]) {
  const completed = new Set(completedLessonIds);
  let latest = null;
  for (const lesson of lessonList) {
    if (completed.has(lesson.id)) latest = lesson;
  }
  return latest;
}

function buildLessonBlock(
  completedLessonIds: readonly string[],
  dueCount: number,
  rand: () => number,
): { block: SessionBlock | null; newLessonId: string | null } {
  if (dueCount > MAX_DUE_FOR_NEW_LESSON) {
    return { block: null, newLessonId: null };
  }
  const lesson = nextUncompletedLesson(completedLessonIds);
  if (lesson === null) {
    return { block: null, newLessonId: null };
  }

  if (lesson.newItemIds.length === 0) {
    // A lesson with no new letters/conjuncts drills its unlocked words
    // and/or sentences instead (e.g. l11/l15/l19/l20 are word-only reviews;
    // l22-fluency is sentence-only — wordIds is empty there, so sampling
    // only from wordIds would produce a zero-exercise block).
    const sentenceIdSet = new Set(lesson.sentenceIds ?? []);
    const pool: string[] = [...lesson.wordIds, ...sentenceIdSet];
    const sampledIds = sampleUnique(pool, LESSON_BLOCK_WORD_COUNT, rand);
    const exercises: Exercise[] = sampledIds.map((itemId) => ({
      kind: sentenceIdSet.has(itemId) ? "sentence-reading" : "word-reading",
      itemId,
    }));
    return {
      block: { kind: "lesson", lessonId: lesson.id, exercises },
      newLessonId: lesson.id,
    };
  }

  const exercises: Exercise[] = [];
  for (const itemId of lesson.newItemIds) {
    exercises.push({ kind: "letter-intro", itemId });
    exercises.push({ kind: "sound-to-symbol", itemId });
  }
  return {
    block: { kind: "lesson", lessonId: lesson.id, exercises },
    newLessonId: lesson.id,
  };
}

/** Union of word/sentence ids unlocked by completed lessons plus the new lesson (if any). */
function unlockedContent(
  completedLessonIds: readonly string[],
  newLessonId: string | null,
): { wordIds: string[]; sentenceIds: string[] } {
  const lessonIds = new Set(completedLessonIds);
  if (newLessonId !== null) lessonIds.add(newLessonId);

  const wordIds = new Set<string>();
  const sentenceIds = new Set<string>();
  for (const id of lessonIds) {
    const lesson = lessonById.get(id);
    if (lesson === undefined) continue;
    for (const wordId of lesson.wordIds) wordIds.add(wordId);
    for (const sentenceId of lesson.sentenceIds ?? [])
      sentenceIds.add(sentenceId);
  }
  return { wordIds: [...wordIds], sentenceIds: [...sentenceIds] };
}

/**
 * Union of letter/sign/conjunct ids taught by completed lessons plus the new
 * lesson (if any) — every `newItemIds` entry ever introduced so far. Powers
 * the paper-block letter-fill: when too few words are unlocked to fill the
 * paper block, taught letters stand in so handwriting practice never has to
 * wait for vocabulary.
 */
function taughtLetterIds(
  completedLessonIds: readonly string[],
  newLessonId: string | null,
): string[] {
  const lessonIds = new Set(completedLessonIds);
  if (newLessonId !== null) lessonIds.add(newLessonId);

  const letterIds = new Set<string>();
  for (const id of lessonIds) {
    const lesson = lessonById.get(id);
    if (lesson === undefined) continue;
    for (const itemId of lesson.newItemIds) letterIds.add(itemId);
  }
  return [...letterIds];
}

function buildReadingBlock(
  wordIds: readonly string[],
  sentenceIds: readonly string[],
  size: number,
  reviewByItemId: ReadonlyMap<string, ReviewState>,
  rand: () => number,
): SessionBlock | null {
  const allIds: string[] = [...wordIds, ...sentenceIds];
  if (allIds.length === 0) return null;

  const sentenceIdSet = new Set(sentenceIds);
  const sampled = weightedSample(allIds, size, reviewByItemId, rand);
  const exercises: Exercise[] = sampled.map((itemId) => ({
    kind: sentenceIdSet.has(itemId) ? "sentence-reading" : "word-reading",
    itemId,
  }));
  return { kind: "reading", exercises };
}

/**
 * Writing block: up to {@link WRITING_BUILDER_MAX} word-builder exercises
 * drawn from the unlocked words, seeded and weighted toward struggling items
 * (the same {@link weightedSample} util the reading block uses). On-screen
 * tracing was removed (spec addendum 2026-07-04) — orthographic recall via
 * the word builder is the screen writing practice now. Omitted entirely when
 * no words are unlocked yet, mirroring the reading-block omission.
 */
function buildWritingBlock(
  wordIds: readonly string[],
  reviewByItemId: ReadonlyMap<string, ReviewState>,
  rand: () => number,
): SessionBlock | null {
  if (wordIds.length === 0) return null;

  const builderIds = weightedSample(
    wordIds,
    WRITING_BUILDER_MAX,
    reviewByItemId,
    rand,
  );
  const exercises: Exercise[] = builderIds.map((itemId) => ({
    kind: "word-builder",
    itemId,
  }));
  return { kind: "writing", exercises };
}

/**
 * Build the paper-dictation block: real handwriting practice, dictated by
 * the app. Prefers unlocked words (recent-lesson words first, then the rest
 * of the unlocked pool, both seeded-shuffled) to fill up to
 * {@link PAPER_DICTATION_COUNT} slots; if fewer than that many words are
 * unlocked (including zero — a fresh-ish learner with no words yet), the
 * remaining slots are filled with a seeded sample of already-taught letters
 * (`taughtLetterIds`) so paper dictation is never skipped just because
 * vocabulary hasn't unlocked. Returns `null` only when there is truly
 * nothing to dictate (no words AND no taught letters) — the caller omits
 * the block entirely in that case rather than rendering an empty one.
 */
export function buildPaperBlock(
  wordIds: readonly string[],
  recentLessonWordIds: readonly string[],
  taughtLetterIds: readonly string[],
  rand: () => number,
): SessionBlock | null {
  // Prefer recent lesson words first, then fill from the rest of the unlocked pool.
  const recentSet = new Set(recentLessonWordIds);
  const preferred = wordIds.filter((id) => recentSet.has(id));
  const rest = wordIds.filter((id) => !recentSet.has(id));

  const shuffledPreferred = shuffle(preferred, rand);
  const shuffledRest = shuffle(rest, rand);
  const ordered = [...shuffledPreferred, ...shuffledRest];
  const pickedWords = ordered.slice(0, PAPER_DICTATION_COUNT);

  const remaining = PAPER_DICTATION_COUNT - pickedWords.length;
  const pickedLetters =
    remaining > 0 ? sampleUnique(taughtLetterIds, remaining, rand) : [];

  const picked: string[] = [...pickedWords, ...pickedLetters];
  if (picked.length === 0) return null;

  const exercises: Exercise[] = picked.map((itemId) => ({
    kind: "paper-dictation",
    itemId,
  }));
  return { kind: "paper", exercises };
}

/** Assemble a single day's practice plan from a learner's current state. */
export function assembleSession(input: AssembleInput): SessionPlan {
  const rand = mulberry32(input.seed);
  const cap = REVIEW_CAP[input.settings.length];
  const readingSize = READING_SIZE[input.settings.length];

  const reviewByItemId = new Map(
    input.reviews.map((state) => [state.itemId, state]),
  );

  const { block: reviewBlock, dueCount } = buildReviewBlock(
    input.reviews,
    input.now,
    cap,
  );

  const { block: lessonBlock, newLessonId } = buildLessonBlock(
    input.completedLessonIds,
    dueCount,
    rand,
  );

  const { wordIds, sentenceIds } = unlockedContent(
    input.completedLessonIds,
    newLessonId,
  );

  const readingBlock = buildReadingBlock(
    wordIds,
    sentenceIds,
    readingSize,
    reviewByItemId,
    rand,
  );

  const blocks: SessionBlock[] = [];
  if (reviewBlock !== null) blocks.push(reviewBlock);
  if (lessonBlock !== null) blocks.push(lessonBlock);
  if (readingBlock !== null) blocks.push(readingBlock);

  const isPaperDay = (input.sessionCount + 1) % input.settings.paperEvery === 0;
  if (isPaperDay) {
    // "Recent lesson" prefers today's newly-introduced lesson (freshest
    // exposure); if the review backlog suppressed a new lesson this session,
    // fall back to the most curriculum-advanced completed lesson.
    const recentLesson =
      newLessonId !== null
        ? lessonById.get(newLessonId)
        : mostRecentCompletedLesson(input.completedLessonIds);
    const recentLessonWordIds = recentLesson?.wordIds ?? [];
    const letterPool = taughtLetterIds(input.completedLessonIds, newLessonId);
    const paperBlock = buildPaperBlock(
      wordIds,
      recentLessonWordIds,
      letterPool,
      rand,
    );
    // Omitted entirely (not pushed as an empty block) when nothing has been
    // taught yet at all — an absolute-fresh-start edge case.
    if (paperBlock !== null) blocks.push(paperBlock);
  } else {
    // Omitted when no words are unlocked yet, mirroring the reading block.
    const writingBlock = buildWritingBlock(wordIds, reviewByItemId, rand);
    if (writingBlock !== null) blocks.push(writingBlock);
  }

  blocks.push({ kind: "summary", exercises: [] });

  return { blocks, newLessonId };
}

/**
 * Pick `count` distractor letter ids for `targetId`: the target's confusables
 * first (seeded shuffle), then filled with seeded picks of other letters of
 * the same `kind`. Never includes the target; always unique.
 */
export function pickDistractors(
  targetId: string,
  count: number,
  rand: () => number,
): string[] {
  const target = letterById.get(targetId);
  if (target === undefined) return [];

  const confusables = target.confusableIds.filter((id) => id !== targetId);
  const shuffledConfusables = shuffle(confusables, rand);

  const result: string[] = [];
  const seen = new Set<string>([targetId]);
  for (const id of shuffledConfusables) {
    if (seen.has(id)) continue;
    seen.add(id);
    result.push(id);
    if (result.length >= count) return result;
  }

  const sameKindPool: string[] = [];
  for (const letter of letterById.values()) {
    if (letter.kind === target.kind && !seen.has(letter.id)) {
      sameKindPool.push(letter.id);
    }
  }
  const shuffledPool = shuffle(sameKindPool, rand);
  for (const id of shuffledPool) {
    if (seen.has(id)) continue;
    seen.add(id);
    result.push(id);
    if (result.length >= count) break;
  }

  return result;
}
